import type { ListEarningsResponse, OptionRight } from '@fleece/models';
import { Play } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button, Field, Input, Spinner } from '@/components/ui';
import { usePreferences } from '@/hooks/use-preferences';
import * as fmt from '@/lib/format';
import { OPTION_HISTORY_START, spreadFigures, type BacktestForm, type PickedSpread } from '@/lib/spread';

export interface SpreadPanelProps {
  readonly right: OptionRight;
  readonly spread: PickedSpread;
  readonly initialForm: BacktestForm;
  /** The underlying's earnings calendar; undefined while it loads, or if it failed. */
  readonly earnings?: ListEarningsResponse;
  /** Why the earnings calendar could not be loaded, when it could not. */
  readonly earningsError?: string;
  readonly running: boolean;
  readonly error?: string;
  readonly onRun: (form: BacktestForm) => void;
}

/**
 * The picked spread, what it collects today, and the backtest that imitates it. The form
 * starts from the spread — its DTE and moneyness, widened — and is re-seeded whenever a
 * different pair is picked, because the page keys this component by the pair.
 */
export function SpreadPanel({ right, spread, initialForm, earnings, earningsError, running, error, onRun }: SpreadPanelProps) {
  const [form, setForm] = useState<BacktestForm>(initialForm);
  const { date } = usePreferences();
  const figures = spreadFigures(spread, right);
  const letter = right === 'put' ? 'P' : 'C';

  const number = (key: keyof BacktestForm) => (value: string) => setForm((previous) => ({ ...previous, [key]: value === '' ? Number.NaN : Number(value) }));
  const text = (key: keyof BacktestForm) => (value: string) => setForm((previous) => ({ ...previous, [key]: value }));
  const invalid = [form.takeProfitPct, form.stopLossPct, form.dteMin, form.dteMax, form.moneynessMinPct, form.moneynessMaxPct].some((v) => !Number.isFinite(v));

  return (
    <div className="flex flex-col gap-2.5 p-2.5">
      <div>
        <div className="text-sm font-semibold">
          −{fmt.strike(spread.short.strike)}
          {letter} / +{fmt.strike(spread.long.strike)}
          {letter}
          <span className="ml-1.5 text-xs font-normal text-ink-2">
            {date(spread.short.expiration)} · {spread.short.dte} DTE
          </span>
        </div>
        <dl className="mt-1.5 grid grid-cols-3 gap-x-2 gap-y-1 text-[11px] tabular">
          <Figure label="Credit (natural)" value={fmt.price(figures.naturalCredit)} />
          <Figure label="Credit (mid)" value={fmt.price(figures.midCredit)} />
          <Figure label="Width" value={fmt.price(figures.width)} />
          <Figure label="Max loss" value={fmt.price(figures.maxLoss)} />
          <Figure label="Credit / width" value={fmt.pct(figures.creditToWidth)} />
          <Figure label="Breakeven" value={fmt.price(figures.breakeven)} />
          <Figure label="Short OTM" value={fmt.pct(spread.short.moneyness)} />
          <Figure label="Short Δ" value={spread.short.delta === undefined ? fmt.NA : spread.short.delta.toFixed(2)} />
          <Figure label="Leg spreads" value={`${fmt.pct(spread.short.spreadPct, 0)} / ${fmt.pct(spread.long.spreadPct, 0)}`} />
        </dl>
      </div>

      <form
        className="flex flex-col gap-2 border-t border-line pt-2.5"
        onSubmit={(event) => {
          event.preventDefault();
          // A box that cannot apply is never sent ticked, whatever it was left at.
          onRun({ ...form, avoidEarnings: form.avoidEarnings && earnings?.coverage === 'reporting' });
        }}
      >
        <div className="text-xs font-semibold tracking-wide text-ink-2 uppercase">Backtest</div>
        <Row>
          <Field label="Take profit" hint="% of credit kept">
            <Input type="number" inputMode="decimal" min={1} max={100} step={1} value={show(form.takeProfitPct)} onChange={(e) => number('takeProfitPct')(e.target.value)} />
          </Field>
          <Field label="Stop loss" hint="loss, % of credit">
            <Input type="number" inputMode="decimal" min={1} max={1000} step={1} value={show(form.stopLossPct)} onChange={(e) => number('stopLossPct')(e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="DTE from">
            <Input type="number" inputMode="numeric" min={1} max={90} value={show(form.dteMin)} onChange={(e) => number('dteMin')(e.target.value)} />
          </Field>
          <Field label="DTE to">
            <Input type="number" inputMode="numeric" min={1} max={90} value={show(form.dteMax)} onChange={(e) => number('dteMax')(e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="Short OTM from" hint="%">
            <Input type="number" inputMode="decimal" min={0} max={50} step="any" value={show(form.moneynessMinPct)} onChange={(e) => number('moneynessMinPct')(e.target.value)} />
          </Field>
          <Field label="Short OTM to" hint="%">
            <Input type="number" inputMode="decimal" min={0} max={50} step="any" value={show(form.moneynessMaxPct)} onChange={(e) => number('moneynessMaxPct')(e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="Entry" hint="ET">
            <Input type="time" min="09:30" max="15:59" value={form.entryTime} onChange={(e) => text('entryTime')(e.target.value)} />
          </Field>
          <Field label="Half-day entry" hint="ET">
            <Input type="time" min="09:30" max="12:59" value={form.shortDayEntryTime} onChange={(e) => text('shortDayEntryTime')(e.target.value)} />
          </Field>
        </Row>
        <Row>
          <Field label="First entry">
            <Input type="date" min={OPTION_HISTORY_START} value={form.from} onChange={(e) => text('from')(e.target.value)} />
          </Field>
          <Field label="Last entry">
            <Input type="date" min={OPTION_HISTORY_START} value={form.to} onChange={(e) => text('to')(e.target.value)} />
          </Field>
        </Row>
        <EarningsToggle
          earnings={earnings}
          error={earningsError}
          checked={form.avoidEarnings}
          onChange={(checked) => setForm((previous) => ({ ...previous, avoidEarnings: checked }))}
        />
        <p className="text-[11px] leading-snug text-muted">
          Every session at the entry time, every spread in range is opened at its last prints, each leg crossed by its bid/ask spread today. Exits are checked from the next
          session, in minutes both legs traded, as resting orders.
        </p>
        {error !== undefined && <p className="text-[11px] text-negative">{error}</p>}
        <Button type="submit" variant="primary" disabled={running || invalid}>
          {running ? <Spinner className="border-accent-ink/30 border-t-accent-ink" /> : <Play className="size-3.5" />}
          {running ? 'Running…' : 'Run backtest'}
        </Button>
      </form>
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-2">{children}</div>;
}

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="truncate text-muted">{label}</dt>
      <dd className="font-medium text-ink">{value}</dd>
    </div>
  );
}

function show(value: number): string | number {
  return Number.isFinite(value) ? value : '';
}

/**
 * Skip entries whose life covers an earnings reaction. Offered only where the calendar can
 * answer: a fund has no reports to avoid, and for a name it does not cover, "none found"
 * would be a guess, so the box is disabled and says why rather than quietly doing nothing.
 */
function EarningsToggle({ earnings, error, checked, onChange }: { earnings?: ListEarningsResponse; error?: string; checked: boolean; onChange: (checked: boolean) => void }) {
  const { date } = usePreferences();
  const available = earnings?.coverage === 'reporting';
  const note =
    earnings === undefined
      ? // No data is either still loading or failed; saying "loading" for a failure would wait forever.
        error !== undefined
        ? `The earnings calendar could not be loaded: ${error}`
        : 'Loading the earnings calendar…'
      : earnings.coverage === 'non-reporting'
        ? `${earnings.symbol} is a fund and reports no earnings.`
        : earnings.coverage === 'unknown'
          ? `There is no earnings calendar for ${earnings.symbol}.`
          : `Calendar complete ${date(earnings.completeFrom)} to ${date(earnings.completeThrough)}; a spread expiring later is skipped, since a report could be missing.`;
  return (
    <label className={available ? 'flex cursor-pointer items-start gap-2' : 'flex cursor-not-allowed items-start gap-2 opacity-60'}>
      <input type="checkbox" checked={available && checked} disabled={!available} onChange={(event) => onChange(event.target.checked)} className="mt-0.5 accent-[var(--accent)]" />
      <span className="flex flex-col gap-0.5">
        <span className="text-xs text-ink">Avoid earnings</span>
        <span className="text-[11px] leading-snug text-muted">Skip an entry whose time to expiration covers an earnings reaction. {note}</span>
      </span>
    </label>
  );
}
